/**
 * Tenant Vapi credentials API service (V4.1 — voice roadmap).
 *
 * The tenant brings its own Vapi API key (BYO). Reads only ever return
 * metadata (whether a key/webhook secret/public key is set) — the backend
 * never returns secret values once stored. Tenant is resolved server-side
 * from the authenticated context (the `x-tenant-id` header the API client
 * already attaches), so no tenantId param is needed here.
 *
 * Pure async functions — no React state, no hooks.
 */
import { get, put, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export interface VapiCredentialMetadata {
  configured: boolean;
  keyNames: string[];
  hasWebhookSecret: boolean;
  hasPublicKey: boolean;
  defaultPhoneNumberId: string | null;
  updatedAt?: string;
}

export interface SetVapiCredentialsPayload {
  /** Required to (re)connect — also how the key is rotated (re-submit a new value). */
  apiKey: string;
  webhookSecret?: string;
  /** Vapi's client-safe "public key" — used by the web voice widget, not a secret. */
  publicKey?: string;
  defaultPhoneNumberId?: string;
}

export const vapiCredentialsService = {
  /** Current connection status — never the secret values. */
  get(): Promise<VapiCredentialMetadata> {
    return get<VapiCredentialMetadata>(endpoints.vapiCredentials);
  },

  /** Store/replace the tenant's Vapi credentials. Re-submitting rotates the key. */
  set(payload: SetVapiCredentialsPayload): Promise<VapiCredentialMetadata> {
    return put<VapiCredentialMetadata>(endpoints.vapiCredentials, payload);
  },

  /** Disconnect Vapi for this tenant (deletes the stored credentials). */
  remove(): Promise<void> {
    return del(endpoints.vapiCredentials);
  },
} as const;
